import { type FC, useState } from "react"
import { useNavigate } from "react-router"
import styled from "styled-components"

import { getExtensionByFileName } from "@forento/core/utilities/file"
import Form from "@forento/ui/components/Form"
import Tooltip from "@forento/ui/components/Tooltip"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"
import { cn } from "@forento/ui/utilities/tailwind"

import { SubmitButton } from "~/components/Button"
import FileChooser from "~/components/FileChooser"
import { CheckmarkIcon, CloseIcon, SettingsIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import Layout, { HelpCenter, HelpCenterIcon, PageBreadcrumb, PageHeader } from "~/components/Layout"
import LoadingIndicator from "~/components/LoadingIndicator"
import ManageTagsModal from "~/components/ManageTagsModal"
import ProductPricing from "~/components/ProductPricing"
import RadioButtons from "~/components/RadioButtons"
import RichContentEditor from "~/components/RichContentEditor"
import useFileUpload from "~/hooks/useFileUpload"
import usePrice from "~/hooks/usePrice"
import useProductPublishedAlert from "~/hooks/useProductPublishedAlert"
import { exportRichContent, type RichContentValue } from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const options = [
	{ id: "upload", label: "Upload file" },
	{ id: "link", label: "Link" },
] as const

const CreateDownloadablePage: FC = () => {
	const navigate = useNavigate()
	const publishedAlert = useProductPublishedAlert("downloadable")

	const [name, setName] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const longDescription = useRichTextArea({ label: "Description" })
	const [presentation, setPresentation] = useState<RichContentValue | null>(null)
	const [isSignInRequired, setSignInRequired] = useState(false)
	const { price, priceValue, setPrice } = usePrice()
	const [isManualOnly, setManualOnly] = useState(false)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(false)
	const [membershipIds, setMembershipIds] = useState<number[]>([])
	const [isPaused, setPaused] = useState(false)
	const [option, setOption] = useState<(typeof options)[number]["id"]>("upload")
	const [downloadable, setDownloadable] = useState<File | null>(null)
	const [link, setLink] = useState("")
	const [selectedTags, setSelectedTags] = useState<number[]>([])

	const [isManageTagsModalOpen, setManageTagsModalOpen] = useState(false)
	const [isSubmitting, setSubmitting] = useState(false)

	const thumbnail = useFile(-1, null)

	const fileUpload = useFileUpload()

	const tags = query.downloadable.listTags.useQuery()

	const handleSubmit = async () => {
		if (name.trim().length === 0) return

		try {
			if (option === "upload") {
				if (!downloadable) setSubmitting(false)
				if (!downloadable) return
				setSubmitting(true)
				const uploaded = await fileUpload.upload({
					file: downloadable,
					kind: "downloadable-file",
					fileExtension: getExtensionByFileName(downloadable.name) ?? undefined,
				})
				if (uploaded === null) {
					setSubmitting(false)
					return
				}
				const created = await trpc.downloadable.create.mutate({
					name,
					shortDescription,
					longDescription: await longDescription.exportEditorState(),
					presentation: exportRichContent(presentation),
					isSignInRequired,
					price: priceValue,
					isManualOnly,
					isBokaderoMemberRequired,
					membershipIds,
					isPaused,
					thumbnailDataUrl: await thumbnail.exportToDataUrl(),
					type: "fileUpload",
					fileName: uploaded.fileName,
					tagIds: selectedTags,
				})
				navigate(routes.downloadable.index())
				await publishedAlert.show({ itemId: created.id })
			} else {
				if (link.trim().length === 0) setSubmitting(false)
				if (link.trim().length === 0) return
				setSubmitting(true)
				const created = await trpc.downloadable.create.mutate({
					name,
					shortDescription,
					longDescription: await longDescription.exportEditorState(),
					presentation: exportRichContent(presentation),
					isSignInRequired,
					price: priceValue,
					isManualOnly,
					isBokaderoMemberRequired,
					membershipIds,
					isPaused,
					thumbnailDataUrl: await thumbnail.exportToDataUrl(),
					type: "link",
					url: link,
					tagIds: selectedTags,
				})
				navigate(routes.downloadable.index())
				await publishedAlert.show({ itemId: created.id })
			}
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
		setSubmitting(false)
	}

	return (
		<Layout>
			{tags.data !== undefined && (
				<ManageTagsModal
					type="downloadable"
					isOpen={isManageTagsModalOpen}
					onClose={() => setManageTagsModalOpen(false)}
					onChange={tags.refetch}
					tags={tags.data}
				/>
			)}
			<PageHeader>
				<PageBreadcrumb path={[{ title: "Downloadables", link: routes.downloadable.index() }]} title="Create" />
				<HelpCenter href="https://forento.link/create-downloadable" target="_blank" rel="noreferrer noopener">
					<HelpCenterIcon />
					Learn more about downloadables
				</HelpCenter>
			</PageHeader>
			<Content onSubmit={handleSubmit}>
				<Section>
					<Title>File</Title>
					<RadioButtons
						options={options.map(option => ({ value: option.id, label: option.label }))}
						value={option}
						onChange={value => setOption(options.find(x => x.id === value)?.id ?? "upload")}
					/>
					{option === "upload" && (
						<div>
							<InputLabel>Downloadable file</InputLabel>
							<FileChooser type="file" value={downloadable} onChange={setDownloadable} />
						</div>
					)}
					{option === "link" && (
						<div>
							<InputField
								label="Link"
								value={link}
								onChange={setLink}
								placeholder="https://link.to/your-file.pdf"
							/>
						</div>
					)}
				</Section>

				<Section>
					<Title>Basic information</Title>
					<InputField label="Name" value={name} onChange={setName} />
					<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
					{longDescription.element}
					<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
				</Section>

				<Section>
					<Title>Pricing</Title>
					<ProductPricing
						type="downloadable"
						isSignInRequired={isSignInRequired}
						setSignInRequired={setSignInRequired}
						price={price}
						setPrice={setPrice}
						isManualOnly={isManualOnly}
						setManualOnly={setManualOnly}
						isBokaderoMemberRequired={isBokaderoMemberRequired}
						setBokaderoMemberRequired={setBokaderoMemberRequired}
						membershipIds={membershipIds}
						setMembershipIds={setMembershipIds}
						isPaused={isPaused}
						setPaused={setPaused}
					/>
				</Section>

				<Section>
					<div className="flex items-center gap-2">
						<Title>Tags</Title>
						<Tooltip tooltip="Manage tags">
							<button
								type="button"
								className="block cursor-pointer"
								onClick={() => setManageTagsModalOpen(true)}
							>
								<SettingsIcon className="h-6 w-6" />
							</button>
						</Tooltip>
					</div>
					<div className="flex flex-wrap gap-2">
						{tags.error ? (
							<p>Failed to load tags.</p>
						) : tags.data === undefined ? (
							<LoadingIndicator size={42} />
						) : (
							tags.data.map(tag => {
								const isSelected = selectedTags.includes(tag.id)
								const Icon = isSelected ? CheckmarkIcon : CloseIcon
								return (
									<button
										key={tag.id}
										type="button"
										className={cn(
											"flex cursor-pointer items-center gap-2 rounded-4xl px-4 py-2 text-white",
											isSelected ? "bg-primary" : "bg-light",
										)}
										onClick={() => toggleStateArray(setSelectedTags, tag.id)}
									>
										<Icon className="size-6" />
										{tag.label}
									</button>
								)
							})
						)}
					</div>
				</Section>

				<Section>
					<Title>Presentation</Title>
					<RichContentEditor
						placeholder="Enter presentation here..."
						initialValue={null}
						setValue={setPresentation}
					/>
				</Section>

				<StyledSubmitButton
					variant="primary"
					isDisabled={
						!(
							name.trim().length > 0 &&
							((option === "upload" && downloadable) || (option === "link" && link.trim().length > 0))
						)
					}
					isLoading={isSubmitting}
				>
					Create downloadable
				</StyledSubmitButton>
			</Content>
		</Layout>
	)
}

const Content = styled(Form)`
	display: flex;
	flex-direction: column;
	gap: 32px;
	margin-bottom: 128px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Title = styled.h2`
	display: block;
	font-weight: 600;
	font-size: 24px;
	color: black;
`

const StyledSubmitButton = styled(SubmitButton)`
	align-self: flex-end;
`

export default CreateDownloadablePage
